import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { request } from '../utils/axios-utils';

export const fetchExpectedShipments = (params) => request({ url: '/shipments/expected', method: 'GET', params });
export const createExpectedShipment = (data) => request({ url: '/shipments/expected', method: 'POST', data });
export const updateExpectedShipment = ({ id, data }) => request({ url: `/shipments/expected/${id}`, method: 'PATCH', data });
export const cancelExpectedShipment = (id) => request({ url: `/shipments/expected/${id}/cancel`, method: 'POST' });
export const fetchShipments = (params) => request({ url: '/shipments', method: 'GET', params });
export const approveShipment = (id) => request({ url: `/shipments/${id}/approve`, method: 'POST' });
export const rejectShipment = ({ id, reason }) => request({ url: `/shipments/${id}/reject`, method: 'POST', data: { reason } });
export const closeUnit = ({ id, outcome, reason }) => request({ url: `/shipments/units/${id}/close`, method: 'POST', data: { outcome, reason } });
export const reopenUnit = (id) => request({ url: `/shipments/units/${id}/reopen`, method: 'POST' });
export const fetchMinibatchReport = (params) => request({ url: '/minibatch/report', method: 'GET', params });
export const fetchMinibatchDay = (params) => request({ url: '/minibatch/day', method: 'GET', params });
export const fetchManpowerDay = (params) => request({ url: '/manpower/day', method: 'GET', params });
export const fetchRawQcPerformance = (params) => request({ url: '/rawQC/performance', method: 'GET', params });
export const fetchAssignmentPerformance = (params) => request({ url: '/assignment/performance', method: 'GET', params });
export const fetchReadyToPack = () => request({ url: '/inventory/outbound/readyToPackSummary', method: 'GET' });
export const fetchPendingAudit = (params) => request({ url: '/inventory/outbound/pendingAudit', method: 'GET', params });
export const fetchPendingOutbound = (params) => request({ url: '/inventory/outbound/pendingOutbound', method: 'GET', params });
export const fetchBoxDay = date => request({ url: '/inventory/outbound/boxDay', method: 'GET', params: date ? { date } : {} });
export const fetchPendingAuditApprovals = () => request({ url: '/inventory/outbound/audits/pending', method: 'GET' });
export const fetchAuditDetail = (boxId) => request({ url: `/inventory/outbound/box/${boxId}/audit/detail`, method: 'GET' });
export const approveAudit = ({ boxId, note }) => request({ url: `/inventory/outbound/box/${boxId}/audit/approve`, method: 'POST', data: { note } });
export const rejectAudit = ({ boxId, note }) => request({ url: `/inventory/outbound/box/${boxId}/audit/reject`, method: 'POST', data: { note } });
export const updateStudioOpsConfig = (data) => request({ url: '/studio/operationsConfig', method: 'PATCH', data });

export const fetchStudioOpsBoard = refresh =>
    request({ url: '/studio-ops/board', method: 'GET', params: refresh ? { refresh: 1 } : undefined });

const heldQuery = {
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
};

export const fetchStudioOpsDetail = params => request({ url: '/studio-ops/board/detail', method: 'GET', params });

export const useStudioOpsBoard = () => {
    const forceRefresh = useRef(false);
    const query = useQuery(
        ['studioOpsBoard'],
        () => {
            const refresh = forceRefresh.current;
            forceRefresh.current = false;
            return fetchStudioOpsBoard(refresh);
        },
        heldQuery
    );
    const reload = () => {
        forceRefresh.current = true;
        return query.refetch();
    };
    return { ...query, reload };
};
export const useStudioOpsDetail = (params, options) =>
    useQuery(['studioOpsDetail', params], () => fetchStudioOpsDetail(params), {
        enabled: !!params?.metric,
        keepPreviousData: true,
        ...heldQuery,
        ...options,
    });
export const fetchSkuJourney = skuId => request({ url: `/minibatch/skuJourney/${skuId}`, method: 'GET' });
export const fetchSkuActivity = (skuId) => request({ url: '/sku/activity', method: 'GET', params: { skuId } });

export const useSkuJourney = (skuId, options) =>
    useQuery(['skuJourney', skuId], () => fetchSkuJourney(skuId), {
        enabled: !!skuId,
        select: res => res?.data?.data || [],
        ...options,
    });

export const useSkuActivity = (skuId, options) =>
    useQuery(['skuActivity', skuId], () => fetchSkuActivity(skuId), {
        enabled: !!skuId,
        select: res => res?.data?.data || [],
        ...options,
    });

export const useExpectedShipments = (params) =>
    useQuery(['expectedShipments', params], () => fetchExpectedShipments(params), { keepPreviousData: true });

export const useReceivedShipments = (params) =>
    useQuery(['receivedShipments', params], () => fetchShipments(params), { keepPreviousData: true });

export const useMinibatchReport = (params, options) =>
    useQuery(['minibatchReport', params], () => fetchMinibatchReport(params), { keepPreviousData: true, ...options });

export const useMinibatchDay = (date, options) =>
    useQuery(['minibatchDay', date], () => fetchMinibatchDay({ date }), { enabled: !!date, keepPreviousData: true, ...options });

export const useManpowerDay = (date, options) =>
    useQuery(['manpowerDay', date], () => fetchManpowerDay({ date }), { enabled: !!date, keepPreviousData: true, ...options });

export const useRawQcPerformance = (params) =>
    useQuery(['rawQcPerformance', params], () => fetchRawQcPerformance(params), {
        keepPreviousData: true,
        refetchInterval: 30000,
    });

export const useAssignmentPerformance = (params) =>
    useQuery(['assignmentPerformance', params], () => fetchAssignmentPerformance(params), {
        keepPreviousData: true,
        refetchInterval: 60000,
    });

export const useReadyToPack = () => useQuery(['readyToPack'], fetchReadyToPack);
export const usePendingAudit = (params) => useQuery(['pendingAudit', params], () => fetchPendingAudit(params));
export const usePendingOutbound = (params) => useQuery(['pendingOutbound', params], () => fetchPendingOutbound(params));
export const useBoxDay = (date, autoReload = false) =>
    useQuery(['boxDay', date], () => fetchBoxDay(date), {
        refetchOnWindowFocus: false,
        refetchInterval: autoReload ? 30000 : false,
        refetchIntervalInBackground: autoReload,
    });
export const usePendingAuditApprovals = () => useQuery(['auditApprovals'], fetchPendingAuditApprovals);
export const useAuditDetail = (boxId, options) =>
    useQuery(['auditDetail', boxId], () => fetchAuditDetail(boxId), { enabled: !!boxId, ...options });

export const useAuditApprovalMutations = () => {
    const qc = useQueryClient();
    const invalidate = () => {
        qc.invalidateQueries('auditApprovals');
        qc.invalidateQueries('auditDetail');
        qc.invalidateQueries('pendingAudit');
        qc.invalidateQueries('pendingOutbound');
    };
    return {
        approve: useMutation(approveAudit, { onSuccess: invalidate }),
        reject: useMutation(rejectAudit, { onSuccess: invalidate }),
    };
};

export const useShipmentMutations = () => {
    const qc = useQueryClient();
    const invalidate = () => {
        qc.invalidateQueries('receivedShipments');
        qc.invalidateQueries('expectedShipments');
    };
    return {
        approve: useMutation(approveShipment, { onSuccess: invalidate }),
        reject: useMutation(rejectShipment, { onSuccess: invalidate }),
        closeUnit: useMutation(closeUnit, { onSuccess: invalidate }),
        reopenUnit: useMutation(reopenUnit, { onSuccess: invalidate }),
        createExpected: useMutation(createExpectedShipment, { onSuccess: invalidate }),
        updateExpected: useMutation(updateExpectedShipment, { onSuccess: invalidate }),
        cancelExpected: useMutation(cancelExpectedShipment, { onSuccess: invalidate }),
        updateOpsConfig: useMutation(updateStudioOpsConfig),
    };
};
